import deepmerge from "deepmerge";
import isOneWebDomainRuntime from "./shared/avaAppRuntime";
import { Environments } from "./shared/constants";

// Helper function to safely get environment variables
const getEnvVar = (key: string): string => {
    return (import.meta.env[key] as string) || "";
};

interface HostnamesConfig {
    identity: string;
    avatax: string;
    cupHost: string;
    ecmApiHost: string;
    docLockerClient: string;
    certCapture: string;
    genCert: string;
    genCertAPI: string;
    certExpress: string;
    dataService: string;
    avaApp?: string;
}

interface EnvironmentConfig {
    cupHost: string;
    ecmApiHost: string;
    docLockerClient: string;
    certCapture: string;
    genCert: string;
    genCertAPI: string;
    certExpress: string;
    fvsConfigurationId: string;
    apiVersion: string;
    apiVersion3: string;
    dataService: string;
}

interface AppConfig {
    appEnv?: string;
    hostnames: HostnamesConfig;
    fvsConfigurationId?: string;
    apiVersion?: string;
    apiVersion3?: string;
    assetsPath?: string;
}

const ecmConfigForAvaApp: Record<Environments, EnvironmentConfig> = {
    [Environments.LOCAL]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web.exemptions.qa.avalara.io",
        docLockerClient: "qa.documentlocker.avalara.io",
        certCapture: "https://staging.certcapture.com",
        genCert: "http://127.0.0.1:9001",
        genCertAPI: "http:localhost:7153",
        certExpress: "https://staging.certexpress.com",
        fvsConfigurationId: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
    },
    [Environments.DEV]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web-exemptions.gamma.dev.us-west-2.aws.avalara.io",
        docLockerClient: "dev.documentlocker.avalara.io",
        certCapture: "https://staging.certcapture.com",
        genCert: "https://dev.gencert.exemptions.avalara.io",
        genCertAPI: "https://api.exemptions.dev.avalara.io",
        certExpress: "https://test-cc7-ce.dev.certcapture.com",
        fvsConfigurationId: "16e6001b-95d2-42de-8b3a-185a15b444af",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
    },
    [Environments.QA]: {
        cupHost: "qa.admin.web.avalara.io",
        ecmApiHost: "api-web.exemptions.qa.avalara.io",
        docLockerClient: "qa.documentlocker.avalara.io",
        certCapture: "https://staging.certcapture.com",
        genCert: "https://qa.gencert.exemptions.avalara.io",
        genCertAPI: "https://api.exemptions.qa.avalara.io",
        certExpress: "https://staging.certexpress.com",
        fvsConfigurationId: "ab64032e-ebaf-4cb4-aa51-ad15464953c0",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://dataservice.gamma.qa.us-west-2.aws.avalara.io",
    },
    [Environments.SBX]: {
        cupHost: "sandbox.admin.avalara.com",
        ecmApiHost: "api.sbx.web.exemptions.avalara.com",
        docLockerClient: "sbx.documentlocker.avalara.com",
        certCapture: "https://sbx.certcapture.com",
        genCert: "https://api.sbx.exemptions.avalara.com",
        genCertAPI: "https://api.sbx.exemptions.avalara.com",
        certExpress: "https://sbx.certexpress.com",
        fvsConfigurationId: "fc747193-a22a-405e-bc06-0c582fde6a98",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://sandbox.data.webplatform.avalara.com",
    },
    [Environments.PRD]: {
        cupHost: "admin.avalara.com",
        ecmApiHost: "api.web.exemptions.avalara.com",
        docLockerClient: "documentlocker.avalara.com",
        certCapture: "https://app.certcapture.com",
        genCert: "https://api.exemptions.avalara.com",
        genCertAPI: "https://api.exemptions.avalara.com",
        certExpress: "https://app.certexpress.com",
        fvsConfigurationId: "13dc2391-5825-46a3-a31e-afb7d50f11d2",
        apiVersion: "v2.5",
        apiVersion3: "v3",
        dataService: "https://data.webplatform.avalara.com",
    },
};

export default function getConfig(): AppConfig {
    // when running in AvaApp do not use configs from environment variables (.env files)
    if (isOneWebDomainRuntime()) {
        const avaAppConfig = window.AvaAppConfig;
        const appEnv = avaAppConfig?.env as Environments;
        const envConfig = appEnv ? ecmConfigForAvaApp[appEnv] : undefined;

        const ecmConfig: AppConfig = {
            appEnv,
            hostnames: {
                identity: avaAppConfig?.hostnames?.identity || "",
                avatax: avaAppConfig?.hostnames?.avatax || "",
                cupHost: envConfig?.cupHost || "",
                ecmApiHost: envConfig?.ecmApiHost || "",
                docLockerClient: envConfig?.docLockerClient || "",
                certCapture: envConfig?.certCapture || "",
                genCert: envConfig?.genCert || "",
                genCertAPI: envConfig?.genCertAPI || "",
                certExpress: envConfig?.certExpress || "",
                dataService: envConfig?.dataService || "",
                avaApp: avaAppConfig?.hostnames?.avaApp || "",
            },
            fvsConfigurationId: envConfig?.fvsConfigurationId,
            apiVersion: envConfig?.apiVersion,
            apiVersion3: envConfig?.apiVersion3,
        };

        return deepmerge(ecmConfig, avaAppConfig || {}) as AppConfig;
    }
    return {
        appEnv: getEnvVar("VITE_ENV"),
        assetsPath: "",
        hostnames: {
            identity: getEnvVar("VITE_IDENTITY_HOST"),
            avatax: getEnvVar("VITE_AVATAX_API_HOST"),
            cupHost: getEnvVar("VITE_CUP_HOST"),
            ecmApiHost: getEnvVar("VITE_API_HOST_BASE"),
            docLockerClient: getEnvVar("VITE_DOCLOCKER_HOST"),
            certCapture: getEnvVar("VITE_CERTCAPTURE"),
            genCert: getEnvVar("VITE_GENCERT_URL"),
            genCertAPI: getEnvVar("VITE_GENCERT_API_URL"),
            certExpress: getEnvVar("VITE_CERTEXPRESS_URL"),
            dataService: getEnvVar("VITE_DATASERVICE_URL"),
        },
        fvsConfigurationId: getEnvVar("VITE_FVS_CONFIGURATIONID"),
        apiVersion: getEnvVar("VITE_API_VERSION"),
        apiVersion3: getEnvVar("VITE_API_VERSION3"),
    };
}
